// apps/platform/src/pages/TenantActivityTypes.tsx
//
// Klubora Platform Admin — Activity Types management for a given tenant.
// Lists `activity_types` filtered by the tenant in the URL, opens
// ActivityTypeDrawer for create / edit, and surfaces deprecate as the only
// destructive action (soft-delete preserves stored extra_fields JSONB on
// existing activities — see plan §R21 / §AE6).

import { useCallback, useEffect, useState, type CSSProperties } from "react";
import { useParams } from "react-router-dom";
import { useTenant } from "../hooks/useTenant";
import {
  listActivityTypes,
  reorderActivityTypes,
  deprecateActivityType,
  type ActivityType,
  type BaseBehavior,
} from "../lib/activityTypesClient";
import { ActivityTypeDrawer } from "../components/ActivityTypeDrawer";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { OpensSelectedTag, TypeChipOrderTable, type OrderColumn } from "../components/TypeChipOrderTable";
import { colors, fontSize, fontWeight, radius, space } from "../lib/tokens";

const BASE_BEHAVIOR_LABEL: Record<BaseBehavior, string> = {
  internal: "Interno",
  external: "Externo",
  trip:     "Viagem",
  online:   "Online",
};

const BASE_BEHAVIOR_BG: Record<BaseBehavior, string> = {
  internal: colors.indigoSoft,
  external: colors.warningSoft,
  trip:     colors.successSoft,
  online:   colors.fog,
};

const BASE_BEHAVIOR_FG: Record<BaseBehavior, string> = {
  internal: colors.indigo,
  external: colors.warning,
  trip:     colors.success,
  online:   colors.stone,
};

export function TenantActivityTypesPage() {
  const { slug = "" } = useParams<{ slug: string }>();
  const { tenant, loading: tenantLoading } = useTenant(slug);
  const [types, setTypes] = useState<ActivityType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerType, setDrawerType] = useState<ActivityType | null>(null);
  const [confirmTarget, setConfirmTarget] = useState<ActivityType | null>(null);
  const [deprecating, setDeprecating] = useState(false);

  const refresh = useCallback(async (tenantId: string) => {
    setLoading(true);
    setError(null);
    try {
      const rows = await listActivityTypes(tenantId);
      setTypes(rows);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Falha ao carregar tipos.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (tenant?.id) refresh(tenant.id);
  }, [tenant?.id, refresh]);

  const openCreate = () => {
    setDrawerType(null);
    setDrawerOpen(true);
  };

  const openEdit = (row: ActivityType) => {
    setDrawerType(row);
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setDrawerType(null);
    if (tenant?.id) refresh(tenant.id);
  };

  const handleDeprecateConfirm = async () => {
    if (!confirmTarget) return;
    setDeprecating(true);
    const res = await deprecateActivityType(confirmTarget.id);
    setDeprecating(false);
    if (res.error) {
      setError(`Erro ao descontinuar: ${res.error}`);
      setConfirmTarget(null);
      return;
    }
    setConfirmTarget(null);
    if (tenant?.id) refresh(tenant.id);
  };

  if (tenantLoading || !tenant) {
    return <div style={{ color: colors.stone }}>Carregando...</div>;
  }

  const columns: OrderColumn<ActivityType>[] = [
    {
      key: "color",
      header: "",
      width: 32,
      render: (r) => (
        <div
          style={{
            width: 18, height: 18, borderRadius: radius.xs,
            background: r.color ?? colors.fog,
            border: `1px solid ${colors.fog}`,
          }}
        />
      ),
    },
    {
      key: "label",
      header: "Tipo",
      render: (r, opensSelected) => (
        <div>
          <div style={{ fontWeight: fontWeight.medium, color: r.deprecated_at ? colors.stone : colors.ink }}>
            {r.label}
            {opensSelected && <OpensSelectedTag />}
          </div>
          <div style={s.subLine}>
            <code style={s.code}>{r.slug}</code>
            {r.icon && <span style={s.iconHint}> · {r.icon}</span>}
          </div>
        </div>
      ),
    },
    {
      key: "base_behavior",
      header: "Comportamento",
      render: (r) => (
        <span
          style={{
            ...s.badge,
            background: BASE_BEHAVIOR_BG[r.base_behavior],
            color: BASE_BEHAVIOR_FG[r.base_behavior],
          }}
        >
          {BASE_BEHAVIOR_LABEL[r.base_behavior]}
        </span>
      ),
    },
    {
      key: "status",
      header: "Status",
      render: (r) => {
        if (r.deprecated_at) {
          return <span style={{ ...s.badge, background: colors.errorSoft, color: colors.error }}>Descontinuado</span>;
        }
        if (!r.enabled) {
          return <span style={{ ...s.badge, background: colors.fog, color: colors.stone }}>Desabilitado</span>;
        }
        return <span style={{ ...s.badge, background: colors.successSoft, color: colors.success }}>Ativo</span>;
      },
    },
    {
      key: "actions",
      header: "",
      align: "right",
      render: (r) => (
        <div style={{ display: "flex", gap: space.xs, justifyContent: "flex-end" }}>
          <button type="button" onClick={() => openEdit(r)} style={s.rowBtn}>Editar</button>
          {!r.deprecated_at && (
            <button
              type="button"
              onClick={() => setConfirmTarget(r)}
              style={s.rowBtnDanger}
            >
              Descontinuar
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div>
      <header style={s.pageHeader}>
        <div>
          <h2 style={s.pageTitle}>Tipos de atividade</h2>
          <p style={s.pageSubtitle}>
            Catálogo de tipos do tenant. O admin do tenant escolhe um destes ao criar uma atividade — o tipo define o comportamento base e os campos extras. A ordem das linhas é a ordem dos chips na aba Atividades do app. Arraste para mudar.
          </p>
        </div>
        <button type="button" onClick={openCreate} style={s.primaryBtn}>+ Novo tipo</button>
      </header>

      {error && <div style={s.errorBox}>{error}</div>}

      <TypeChipOrderTable<ActivityType>
        key={tenant.id}
        tenantId={tenant.id}
        types={types}
        loading={loading}
        onTypesChange={setTypes}
        positionKey="activities_all_chip_position"
        reorder={reorderActivityTypes}
        columns={columns}
        allSpanFrom="label"
        allHint="mostra todas as atividades, de qualquer tipo"
        onRowClick={openEdit}
      />

      {drawerOpen && (
        <ActivityTypeDrawer
          tenantId={tenant.id}
          type={drawerType}
          onClose={closeDrawer}
          onSaved={() => { if (tenant?.id) refresh(tenant.id); }}
        />
      )}

      <ConfirmDialog
        open={!!confirmTarget}
        title="Descontinuar tipo de atividade?"
        message={
          <>
            <strong>{confirmTarget?.label}</strong> ficará indisponível para o admin do tenant criar novas atividades.
            Atividades existentes continuam funcionando normalmente — este é um soft-delete reversível só pela tabela.
          </>
        }
        confirmLabel="Descontinuar"
        variant="danger"
        loading={deprecating}
        onConfirm={handleDeprecateConfirm}
        onCancel={() => setConfirmTarget(null)}
      />
    </div>
  );
}

const s: Record<string, CSSProperties> = {
  pageHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: space.lg,
    marginBottom: space.lg,
  },
  pageTitle: {
    margin: 0,
    fontSize: fontSize.h2,
    fontWeight: fontWeight.medium,
    color: colors.ink,
    letterSpacing: "-0.01em",
  },
  pageSubtitle: {
    margin: `${space.xs}px 0 0 0`,
    fontSize: fontSize.md,
    color: colors.stone,
    maxWidth: 640,
    lineHeight: 1.5,
  },
  primaryBtn: {
    height: 36,
    padding: "0 16px",
    background: colors.indigo,
    color: colors.white,
    border: "none",
    borderRadius: radius.sm,
    fontSize: fontSize.md,
    fontWeight: fontWeight.medium,
    cursor: "pointer",
    flexShrink: 0,
  },
  subLine: {
    marginTop: 2,
    fontSize: fontSize.xs,
    color: colors.stone,
  },
  code: {
    fontFamily: "JetBrains Mono, monospace",
    fontSize: fontSize.xs,
  },
  iconHint: {
    color: colors.stoneSoft,
  },
  badge: {
    display: "inline-block",
    padding: "2px 8px",
    borderRadius: radius.pill,
    fontSize: fontSize.xs,
    fontWeight: fontWeight.medium,
  },
  rowBtn: {
    padding: `${space.xs}px ${space.md}px`,
    background: "transparent",
    border: `1px solid ${colors.fog}`,
    borderRadius: radius.sm,
    color: colors.ink,
    fontSize: fontSize.sm,
    cursor: "pointer",
  },
  rowBtnDanger: {
    padding: `${space.xs}px ${space.md}px`,
    background: "transparent",
    border: `1px solid ${colors.fog}`,
    borderRadius: radius.sm,
    color: colors.error,
    fontSize: fontSize.sm,
    cursor: "pointer",
  },
  errorBox: {
    color: colors.error,
    fontSize: fontSize.sm,
    marginBottom: space.md,
    background: colors.errorSoft,
    padding: space.sm,
    borderRadius: radius.sm,
  },
};
